import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import {
  MatFormField,
  MatInput,
  MatLabel,
  MatPrefix,
  MatSuffix
} from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import {
  Observable,
  Subject,
  Subscription,
  debounceTime,
  forkJoin,
  of,
  take,
  takeWhile
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap
} from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../../../components/confirmation-dialog/confirmation-dialog.component';
import { InlineSvgComponent } from '../../../../components/inline-svg/inline-svg.component';
import { ShareModalComponent } from '../../../../components/share-modal/share-modal.component';
import { UserLogoBundleComponent } from '../../../../components/user-logo-bundle/user-logo-bundle.component';
import { IntersectionListenerDirective } from '../../../../directives/intersection-listener.directive';
import {
  FilterField,
  FilterGroup,
  SortConfig,
  SortDirection
} from '../../../../models/filter.models';
import { Area, Group } from '../../../../models/general.models';
import {
  UploadQueryParams,
  UploadResponse
} from '../../../../models/project-upload.model';
import { ChildProject, Project } from '../../../../models/project.module';
import { User } from '../../../../models/user.model';
import { MetersSquareToKilometersSquare } from '../../../../pipes/m2_to_km2.pipe';
import { MetersToKilometers } from '../../../../pipes/m_to_km.pipe';
import { AreaSearchHit, AreaService } from '../../../../services/area.service';
import { LayerService } from '../../../../services/layer.service';
import { NewProjectService } from '../../../../services/newProject.service';
import { ProjectUploadService } from '../../../../services/project-upload.service';
import { ProjectService } from '../../../../services/project.service';
import { TaskService } from '../../../../services/task.service';
import { ToastNotificationService } from '../../../../services/toast-notification.service';
import { MapCommunicatorService } from '../../map-communicator.service';
import { DeleteAreaDialogComponent } from '../delete-area-dialog/delete-area-dialog.component';
import { DownloadDialogComponent } from '../download-dialog/download-dialog.component';
import { FileProcessingStatusComponent } from '../file-processing-status/file-processing-status.component';
import { ImportFilesDialogComponent } from '../import-files-dialog/import-files-dialog.component';
import {
  MoveDialogComponent,
  MoveDialogData
} from '../move-dialog/move-dialog.component';
import { RiskReportModalComponent } from '../risk-report-modal/risk-report-modal.component';
import { SaveAreaDialogComponent } from '../save-area-dialog/save-area-dialog.component';
import { CreateProjectModalComponent } from './create-project-modal/create-project-modal.component';
import { FilterComponent } from './filter/filter.component';

@Component({
  selector: 'frmg-area-list',
  templateUrl: './area-list.component.html',
  styleUrls: ['./area-list.component.scss'],
  imports: [
    MatMenuTrigger,
    InlineSvgComponent,
    NgClass,
    MatMenu,
    MatMenuItem,
    MatTooltip,
    FilterComponent,
    FileProcessingStatusComponent,
    MatFormField,
    MatPrefix,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatSuffix,
    MatSelect,
    FormsModule,
    MatOption,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NgTemplateOutlet,
    MatCheckbox,
    MatButton,
    IntersectionListenerDirective,
    MetersSquareToKilometersSquare,
    MetersToKilometers,
    UserLogoBundleComponent
  ]
})
export class AreaListComponent implements OnInit, OnDestroy {
  private isMapCreated: boolean = true;

  public searchAreaCtrl: FormControl;
  public currentUser: User;
  public showSearch: boolean = false;
  public showSearchResults: boolean = false;
  public list: Array<Area & { selected?: boolean; highlighted?: boolean }> = [];
  public editingArea?: Area & { selected?: boolean; highlighted?: boolean };
  public editingProject?: ChildProject;
  public projects: Project[] = [];
  public currentProject: Project;
  public searchType: string = 'all';
  public searchResults: Array<Project | AreaSearchHit> = [];
  public searchFinished: boolean;
  public selectAll: boolean = false;
  public excludeSelectedArray = new Set<string>();
  public includeSelectedArray = new Set<string>();
  public showUploadIcon: boolean;
  public uploads: UploadResponse[] = [];
  public areasLoadingByScroll = signal(false);
  public areasLoading = signal(false);
  public projectsLoading = signal(false);
  public loadingData = computed(
    () => this.areasLoading() || this.projectsLoading()
  );
  private limit = 20;
  private offset = 0;
  private offsetTop = 0;
  private readonly destroyRef = inject(DestroyRef);
  protected rootProjectId: string;
  private subPath: string;
  private rootProjectDetailsRequested = new Set<string>();
  private isLoadingProjects = false; // Prevent concurrent loadProjects calls
  public rootState = 'root';
  public sortState = 'created_at:asc';
  public routerSubscription: Subscription;
  private searchQuery$ = new Subject<{ query: string; projectId?: string }>();

  @ViewChild('listContainer') listContainer: ElementRef<HTMLElement>;
  @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;
  @ViewChild('filterTrigger') filterTrigger: MatMenuTrigger;
  @ViewChild('orderTrigger') orderTrigger: MatMenuTrigger;
  @ViewChild('searchInput', { static: false, read: ElementRef })
    searchInput: ElementRef;

  @Output() projectSelect = new EventEmitter<Project | null>();
  @Output() onDestroyCallback = new EventEmitter<Record<
    string,
    unknown
  > | null>();

  @Input() initState: {
    list: Array<Area & { selected?: boolean; highlighted?: boolean }>;
    projects: Project[];
    currentProject: Project;
    rootState: string;
    path: string;
  };

  constructor(
    private fb: FormBuilder,
    public areaService: AreaService,
    private projectService: ProjectService,
    private newProjectService: NewProjectService,
    private mapCommunicatorService: MapCommunicatorService,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private layerService: LayerService,
    private taskService: TaskService,
    private toastNotificationService: ToastNotificationService,
    private uploadService: ProjectUploadService,
    private router: Router
  ) {
    this.searchAreaCtrl = this.fb.control('');
    this.currentUser = JSON.parse(localStorage.getItem('user')) as User;
  }

  ngOnInit(): void {
    this.selectAll = false;
    this.includeSelectedArray.clear();
    this.excludeSelectedArray.clear();

    const url = this.router.url;
    this.subPath = url.replace(/^\/?risk-map\/?/, '') || '';
    if (this.initState && this.initState.path === this.subPath) {
      this.list = this.initState.list || [];
      this.currentProject = this.initState.currentProject;
      this.projects = this.initState.projects;
      this.rootState = this.initState.rootState;
      this.mapCommunicatorService.currentProject.next(this.currentProject);
    } else {
      if (this.subPath) {
        this.fetchCurrentProject();
        this.loadProjects();
      } else this.loadProjects();
    }

    this.searchAreaCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        if (this.showSearch) {
          this.startSearch(query);
        } else {
          this.resetSearchState();
        }
      });

    this.searchQuery$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(
          (prev, curr) =>
            prev.query === curr.query && prev.projectId === curr.projectId
        ),
        switchMap(({ query, projectId }) =>
          this.performSearch(query, projectId)
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ areaHits, projectHits }) => {
        this.searchResults = [...areaHits, ...projectHits];
        this.searchFinished = true;
        this.cd.detectChanges();
      });

    this.mapCommunicatorService.rectangleSelectedAreas
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids: string[]) => {
        this.selectAll = false;
        this.includeSelectedArray.clear();
        this.excludeSelectedArray.clear();
        if (Array.isArray(ids) && ids.length) {
          ids.forEach((id) => this.includeSelectedArray.add(id));
          this.mapCommunicatorService.selectedAreas.next(
            Array.from(this.includeSelectedArray)
          );
        } else {
          this.mapCommunicatorService.selectedAreas.next([]);
        }
        this.cd.detectChanges();
      });

    this.mapCommunicatorService.mapCommunicator
      .pipe(
        takeWhile(() => this.isMapCreated),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((command) => {
        if (command.startsWith('map_select_')) {
          const id = command.split('map_select_')[1];
          Array.from(this.listContainer.nativeElement.children)
            .find((ch) => ch.id === id)
            ?.scrollIntoView();
          if (this.selectAll) {
            if (this.excludeSelectedArray.has(id)) {
              this.excludeSelectedArray.delete(id);
            } else {
              this.excludeSelectedArray.add(id);
            }
          } else {
            if (this.includeSelectedArray.has(id)) {
              this.includeSelectedArray.delete(id);
            } else {
              this.includeSelectedArray.add(id);
            }
          }
          this.mapCommunicatorService.selectedAreas.next(
            Array.from(this.includeSelectedArray)
          );
          this.mapCommunicatorService.excludedAreas.next(
            Array.from(this.excludeSelectedArray)
          );
          this.cd.detectChanges();
        } else if (command == 'refresh_list') {
          this.offset = 0;
          this.updateProjectData(this.currentProject);
        } else if (command == 'added_new_area') {
          this.areaService.areasList
            .pipe(debounceTime(0), take(1))
            .subscribe((currentList) => {
              this.list.push(currentList[currentList.length - 1]);
            });
        }
      });

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const newSubPath =
          event.urlAfterRedirects.replace(/^\/?risk-map\/?/, '') || '';

        if (newSubPath === this.subPath) {
          return;
        }

        this.subPath = newSubPath;
        if (this.subPath == '') {
          this.currentProject = undefined;
          this.rootProjectId = undefined;
          this.rootProjectDetailsRequested.clear();
          this.mapCommunicatorService.currentProject.next(null);
        } else {
          this.fetchCurrentProject();
        }
        this.loadProjects();
      });

    this.areaService.areasList
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((updatedAreas) => {
        if (
          this.currentProject &&
          !this.currentProject.is_root &&
          updatedAreas.length > 0
        ) {
          const currentFolderId = this.currentProject.id;
          const relevantAreas = updatedAreas.filter(
            (area) =>
              area.properties?.['project_id'] ===
                this.currentProject.project_id &&
              area.properties?.['folder_id'] === currentFolderId
          );

          if (relevantAreas.length > 0) {
            this.list = relevantAreas;
            this.cd.detectChanges();
          }
        }
      });
  }

  fetchCurrentProject() {
    const parentPath = this.subPath.split('/');
    let newPath = '';
    if (parentPath.length > 1) {
      parentPath.pop();
      newPath = parentPath.join('/');
    }

    const state = this.rootState || 'root';
    const sort = this.sortState || 'name:asc';

    this.newProjectService
      .getProjectsByPath(newPath, state, sort)
      .subscribe((resp) => {
        const foundProject = resp.find(
          (project) => project.path === this.subPath
        );
        if (foundProject) {
          this.currentProject = foundProject;
          this.mapCommunicatorService.currentProject.next(this.currentProject);
          this.rootState = this.currentProject?.is_owned_by_current_user
            ? 'root'
            : 'shared';

          if (this.currentProject.is_root || this.currentProject.is_project) {
            if (
              !this.rootProjectId ||
              this.rootProjectId !== this.currentProject.id
            ) {
              this.rootProjectId = this.currentProject.id;
              this.getRootProjectDetails(this.currentProject.id);
            }
          }

          if (!this.currentProject.is_root) {
            this.updateProjectData(this.currentProject);
          }

          this.cd.detectChanges();
        }
      });
  }

  updateSubPath(newPath: string) {
    if (this.subPath === newPath) return;
    this.router.navigate([`/risk-map/${newPath}`]);
    this.subPath = newPath;
  }

  updateRootState(state?: 'root' | 'shared' | 'archived') {
    this.rootState = state;
    this.loadProjects();
  }

  updateSortState(
    state: 'name:desc' | 'created_at:desc' | 'name:asc' | 'created_at:asc'
  ) {
    this.sortState = state;
    // Convert sort string to SortConfig format
    const [field, direction] = state.split(':');
    const sortConfig: SortConfig = {
      field,
      direction: direction as SortDirection
    };
    this.onApplyFilter({ sorts: [sortConfig] });
    this.loadProjects();
  }

  getRootStateIcon(project: Project) {
    if (project?.path?.split('/')?.length > 1) {
      return 'folder.svg';
    }

    switch (this.rootState) {
      case 'shared':
        return 'Shared.svg';
      case 'archived':
        return 'archived.svg';
      default:
        return 'project.svg';
    }
  }

  public getProjectIconPath(project: Project) {
    if (
      project?.user &&
      this.currentUser.email === project.user.email &&
      !project.is_archived
    ) {
      return 'project.svg';
    } else if (project.is_archived) {
      return 'archived.svg';
    } else {
      return 'Shared.svg';
    }
  }

  getRootStateTitle() {
    switch (this.rootState) {
      case 'shared':
        return 'Shared with me';
      case 'archived':
        return 'My Archives';
      default:
        return 'My Projects';
    }
  }

  isFilesWithErrors(): boolean {
    return this.uploads.some((upload) => upload.status === 'failed');
  }

  isFilesWithWarnings(): boolean {
    return this.uploads.some(
      (upload) => upload.status === 'pending_confirmation'
    );
  }

  isFilesHasIssues(): boolean {
    return this.isFilesWithErrors() || this.isFilesWithWarnings();
  }

  public startSearch(query?: string) {
    if (!this.showSearch) {
      this.resetSearchState();
      return;
    }

    const scopedProjectId =
      this.searchType === 'all' ? undefined : this.currentProject?.id;
    this.searchQuery$.next({
      query: query ?? '',
      projectId: scopedProjectId
    });
  }

  ngOnDestroy() {
    this.isMapCreated = false;
    this.areaService.count = 0;
    this.rootProjectDetailsRequested.clear();
    this.onDestroyCallback.emit({
      list: this.list,
      projects: this.projects,
      currentProject: this.currentProject,
      rootState: this.rootState,
      path: this.subPath
    });
  }

  public getSearchResultsByType(type: string) {
    switch (type) {
      case 'projects':
        return this.searchResults.filter(
          (item): item is Project =>
            this.isProjectSearchResult(item) && item.is_project === true
        );
      case 'folders':
        return this.searchResults.filter(
          (item): item is Project =>
            this.isProjectSearchResult(item) && item.is_project === false
        );
      case 'aois':
        return this.searchResults.filter((item): item is AreaSearchHit =>
          this.isAreaSearchResult(item)
        );
      default:
        return this.searchResults;
    }
  }

  private performSearch(
    query: string,
    projectScope?: string
  ): Observable<{ areaHits: AreaSearchHit[]; projectHits: Project[] }> {
    const trimmedQuery = (query ?? '').trim();

    if (!trimmedQuery) {
      this.resetSearchState();
      return of({ areaHits: [], projectHits: [] });
    }

    const areaSearch$ = this.areaService
      .searchAreasNew(trimmedQuery, projectScope)
      .pipe(map((response) => response?.hits ?? []));

    const projectSearch$ = this.projectService
      .searchProjects(trimmedQuery)
      .pipe(map((response) => this.normalizeProjectSearchResponse(response)));

    return forkJoin({
      areaHits: areaSearch$,
      projectHits: projectSearch$
    }).pipe(
      catchError((_error) => {
        this.toastNotificationService.showNotification({
          type: 'error',
          message: 'Search failed. Please try again later.'
        });
        this.resetSearchState();
        return of({ areaHits: [], projectHits: [] });
      })
    );
  }

  private normalizeProjectSearchResponse(response: any): Project[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response as Project[];
    }
    if (Array.isArray(response.items)) {
      return response.items as Project[];
    }
    if (Array.isArray(response.hits)) {
      return response.hits as Project[];
    }
    return [];
  }

  private resetSearchState() {
    this.searchResults = [];
    this.searchFinished = true;
    this.cd.detectChanges();
  }

  private isProjectSearchResult(
    item: Project | AreaSearchHit
  ): item is Project {
    return (
      typeof (item as Project)?.is_project === 'boolean' &&
      typeof (item as Project)?.path === 'string'
    );
  }

  private isAreaSearchResult(
    item: Project | AreaSearchHit
  ): item is AreaSearchHit {
    return (
      !!(item as AreaSearchHit)?.geometry ||
      !!(item as AreaSearchHit)?.properties
    );
  }

  getRootProjectDetails(projectId: string) {
    if (this.rootProjectDetailsRequested.has(projectId)) {
      return;
    }

    this.rootProjectDetailsRequested.add(projectId);

    this.rootProjectId = projectId;

    this.getUploadFilesInfo(false);
  }

  getUploadFilesInfo(openMenu?: boolean): void {
    const now = new Date();
    const shifted = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const projectId = this.currentProject?.id;

    if (!projectId) return;

    const commonParams = { root_project: this.rootProjectId };

    const importantParams: UploadQueryParams = {
      ...commonParams,
      status_in: 'pending_confirmation,failed'
    };

    const recentCompletedParams: UploadQueryParams = {
      ...commonParams,
      status_in: 'completed,pending,progress,started',
      created_after: shifted.toISOString()
    };

    forkJoin([
      this.uploadService.getUploads(projectId, importantParams),
      this.uploadService.getUploads(projectId, recentCompletedParams)
    ]).subscribe({
      next: ([importantRes, recentRes]) => {
        const combined = [...importantRes.uploads, ...recentRes.uploads];

        const statusPriority = {
          pending_confirmation: 0,
          failed: 1,
          other: 2
        };

        this.uploads = combined.sort((a, b) => {
          const getPriority = (status: string) =>
            statusPriority[status] !== undefined
              ? statusPriority[status]
              : statusPriority['other'];

          return getPriority(a.status) - getPriority(b.status);
        });

        this.showUploadIcon = !(this.uploads.length > 0);
        this.cd.markForCheck();

        setTimeout(() => {
          if (openMenu && !this.menuTrigger?.menuOpen) {
            this.menuTrigger?.openMenu();
          }
        }, 100);
      },
      error: (_err) => {
        // Removed console.error for production
      }
    });
  }

  public getUsersWithAccess(project: Project) {
    const users = project.shares.map((share) => share.shared_with_user);
    switch (this.currentUser.email) {
      case project.user.email:
        return [users];
      default:
        return [project.user, ...users];
    }
  }

  public goToRoot() {
    this.currentProject = undefined;
    this.updateSubPath('');
    if (this.subPath === '') {
      this.loadProjects();
    }
  }

  public loadProjects() {
    // Prevent concurrent calls
    if (this.isLoadingProjects) {
      return;
    }
    this.isLoadingProjects = true;
    this.areaService.areasList.next([]);
    this.projectsLoading.set(true);
    this.cd.detectChanges();
    this.newProjectService
      .getProjectsByPath(this.subPath, this.rootState, this.sortState)
      .subscribe(
        (resp) => {
          this.isLoadingProjects = false;
          this.projects = resp || [];
          this.projectsLoading.set(false);
          this.mapCommunicatorService.currentProject.next(this.currentProject);
          this.projectSelect.emit(this.currentProject);

          if (this.currentProject?.id) {
            if (this.currentProject.is_root || this.currentProject.is_project) {
              if (
                !this.rootProjectId ||
                this.rootProjectId !== this.currentProject.id
              ) {
                this.rootProjectId = this.currentProject.id;
                this.getRootProjectDetails(this.currentProject.id);
              }
            }
          }

          this.cd.detectChanges();
        },
        () => {
          this.isLoadingProjects = false;
          this.projectsLoading.set(false);

          if (this.subPath && this.subPath !== '' && this.subPath !== 'root') {
            this.subPath = '';
            this.updateSubPath(this.subPath);
            this.loadProjects();
          } else {
            this.toastNotificationService.showNotification({
              type: 'error',
              message:
                'Such project doesnt exist or you dont have access to it. Please try again.'
            });
          }
        }
      );
  }

  getAreaIconType(area: Area): string {
    switch (area.geometry.type) {
      case 'Circle':
      case 'Polygon':
      case 'MultiPolygon':
        return 'icon icon-polygon-space';
      case 'LineString':
      case 'MultiLineString':
      case 'LinearRing':
        return 'icon icon-line';
      case 'Point':
      case 'MultiPoint':
      default:
        return 'icon icon-point';
    }
  }

  public clearSearch() {
    this.searchAreaCtrl.setValue('', { emitEvent: false });
    this.startSearch('');
  }

  public toggleSearch() {
    this.showSearch = !this.showSearch;
    if (!this.showSearch) {
      this.clearSearch();
    } else {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
        if (this.searchAreaCtrl.value) {
          this.startSearch(this.searchAreaCtrl.value);
        }
      });
    }
  }

  public createGroupString(groups: Group[]) {
    return groups.map((group) => `#${group.name}`).join(' ');
  }

  public showUploadFile(): void {
    const dialog = this.dialog.open(ImportFilesDialogComponent, {
      data: {
        projectId: this.currentProject.id,
        folderName: this.currentProject.name,
        tenantId: this.currentProject.tenant_id,
        teamId: this.currentProject.team_id,
        userId: this.currentUser?.user_id
      }
    });
    dialog.afterClosed().subscribe((_) => {
      this.getUploadFilesInfo(true);
      this.cd.detectChanges();
    });
  }

  public deleteAreas(areas?: Set<string>, alone?: boolean) {
    const selectAll = alone ? false : this.selectAll;
    const project_id = this.currentProject.project_id;
    const exclude_area_ids = alone ? [] : Array.from(this.excludeSelectedArray);
    const include_area_ids = alone
      ? Array.from(areas || [])
      : Array.from(this.includeSelectedArray);

    const dialog = this.dialog.open(DeleteAreaDialogComponent, {
      data: {
        areas,
        selectAll,
        project_id,
        exclude_area_ids,
        include_area_ids,
        alone
      }
    });
    dialog.afterClosed().subscribe((result) => {
      if (!result) return;

      if (result.deleted_count > 0) {
        this.selectAll = false;
        this.includeSelectedArray.clear();
        this.excludeSelectedArray.clear();
        this.mapCommunicatorService.setAllAreas.next(false);

        // Clear area service cache and refresh the area list
        this.areaService.clearAreaList();
        this.offset = 0;
        this.updateProjectData(this.currentProject);

        // Notify map to refresh areas
        this.mapCommunicatorService.mapCommunicator.next(
          'refetch_areas_for_map'
        );

        // Show success notification
        this.toastNotificationService.showNotification({
          type: 'success',
          message: `Successfully deleted ${result.deleted_count} area${
            result.deleted_count !== 1 ? 's' : ''
          }`
        });
      }

      this.cd.detectChanges();
    });
  }

  private checkDeleteStatus(task_id: string) {
    this.taskService.getTaskStatus(task_id).subscribe((resp) => {
      if (resp.status === 'completed') {
        this.selectAll = false;
        this.includeSelectedArray.clear();
        this.excludeSelectedArray.clear();
        this.mapCommunicatorService.setAllAreas.next(false);
        // Clear area service cache and refresh the area list
        this.areaService.clearAreaList();
        this.offset = 0;
        this.updateProjectData(this.currentProject);

        // Notify map to refresh areas
        this.mapCommunicatorService.mapCommunicator.next(
          'refetch_areas_for_map'
        );
      } else {
        this.projectsLoading.set(true);
        setTimeout(() => {
          this.checkDeleteStatus(task_id);
        }, 1500);
      }
    });
  }

  public deleteArea(area: Area) {
    const areaId = area.id || area.properties?.id;
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        data: {
          header: 'Are you sure you want to delete this area?',
          body: 'This will permanently remove it from the list.',
          confirmText: 'Delete'
        }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this.areaService
          .deleteBulkAreas(
            this.currentProject.project_id,
            [],
            [areaId],
            undefined
          )
          .subscribe({
            next: (resp: any) => {
              this.selectAll = false;
              this.includeSelectedArray.clear();
              this.excludeSelectedArray.clear();
              this.mapCommunicatorService.setAllAreas.next(false);

              this.areaService.clearAreaList();
              this.offset = 0;
              this.updateProjectData(this.currentProject);

              this.mapCommunicatorService.mapCommunicator.next(
                'refetch_areas_for_map'
              );

              const count = resp?.deleted_count ?? 1;
              this.toastNotificationService.showNotification({
                type: 'success',
                message: `Successfully deleted ${count} area${
                  count !== 1 ? 's' : ''
                }`
              });
              this.cd.detectChanges();
            },
            error: (error) => {
              this.toastNotificationService.showNotification({
                type: 'error',
                message: error?.error?.detail || 'Failed to delete the area'
              });
            }
          });
      });
  }

  public areaChecked(
    area: Area & { selected?: boolean },
    event?: Event | MatCheckboxChange
  ) {
    if (event && (event as any).stopPropagation) {
      (event as any).stopPropagation();
    }

    if (this.editingArea || !event) return;

    const areaId = area.id || area.properties?.id;

    if (this.selectAll) {
      if (this.excludeSelectedArray.has(areaId)) {
        this.excludeSelectedArray.delete(areaId);
      } else {
        this.excludeSelectedArray.add(areaId);
      }
    } else {
      if (this.includeSelectedArray.has(areaId)) {
        this.includeSelectedArray.delete(areaId);
      } else {
        this.includeSelectedArray.add(areaId);
      }
    }

    this.mapCommunicatorService.selectedAreas.next(
      Array.from(this.includeSelectedArray)
    );
    this.mapCommunicatorService.excludedAreas.next(
      Array.from(this.excludeSelectedArray)
    );

    this.cd.detectChanges();
  }

  public areaHovered(area: Area & { selected?: boolean }) {
    this.mapCommunicatorService.mapCommunicator.next(`hover_${area?.id}_1000`);
  }

  public someChecked(): boolean {
    return this.selectAll
      ? !!this.excludeSelectedArray.size
      : !!this.includeSelectedArray.size;
  }

  public setAll(all: boolean): void {
    this.selectAll = all;
    this.includeSelectedArray.clear();
    this.excludeSelectedArray.clear();
    this.mapCommunicatorService.setAllAreas.next(all);
    this.mapCommunicatorService.selectedAreas.next(
      Array.from(this.includeSelectedArray)
    );
    this.mapCommunicatorService.excludedAreas.next(
      Array.from(this.excludeSelectedArray)
    );
  }

  public isSelected(area: Area) {
    const areaId = area.id || area.properties?.id;
    return this.selectAll
      ? !this.excludeSelectedArray.has(areaId)
      : this.includeSelectedArray.has(areaId);
  }

  public isCheckboxDisabled(area: Area): boolean {
    const areaId = area.id || area.properties?.id;
    return (
      this.editingArea?.id == areaId ||
      this.editingArea?.properties?.id == areaId
    );
  }

  public getCheckedAmount(): number {
    return this.selectAll
      ? this.areaService.count - this.excludeSelectedArray.size
      : this.includeSelectedArray.size;
  }

  public focusOn(area: Area, event: Event) {
    event.stopPropagation();
    this.mapCommunicatorService.geometryToFocus.next({
      geometry: area.geometry
    });
  }

  public downloadSelected() {
    if (!this.getCheckedAmount()) return;
    const data: {
      project_id: string;
      exclude_area_ids?: string[];
      include_area_ids?: string[];
    } = {
      project_id: this.currentProject.project_id
    };
    if (this.selectAll) {
      data.exclude_area_ids = Array.from(this.excludeSelectedArray);
    } else {
      data.include_area_ids = Array.from(this.includeSelectedArray);
    }
    this.layerService.getLayers().subscribe((layers) => {
      this.dialog.open(DownloadDialogComponent, {
        width: '1100px !important',
        data: {
          layerList: layers,
          area_selection: data
        }
      });
    });
  }

  public editArea(area: Area, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(SaveAreaDialogComponent, {
      data: {
        layer: area,
        project: this.currentProject
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.success) {
        this.areaService.clearAreaList();
        this.offset = 0;
        this.updateProjectData(this.currentProject);

        this.mapCommunicatorService.mapCommunicator.next(
          'refetch_areas_for_map'
        );
      }
    });
  }

  public openProject(project: Project, event?: Event): void {
    if (this.editingProject && project.id === this.editingProject.id) return;
    if (project.is_archived) return;
    event?.stopPropagation();
    this.setAll(false);
    this.projectsLoading.set(true);
    this.cd.detectChanges();
    this.offset = 0;
    this.offsetTop = 0;

    // Update rootProjectId when navigating to a root project
    if (project.is_root || project.is_project) {
      this.rootProjectId = project.id;
      this.getRootProjectDetails(project.id);
    }
    // For nested folders, rootProjectId should already be set from the parent

    // Update project data first (but don't let it control projectsLoading)
    this.updateProjectData(project, false);

    // Load child projects/folders
    this.projects = [];
    this.newProjectService
      .getProjectsByPath(project.path, this.rootState, this.sortState)
      .subscribe(
        (resp) => {
          this.projects = resp || [];
          this.projectsLoading.set(false);
          this.includeSelectedArray.clear();
          this.excludeSelectedArray.clear();
          this.mapCommunicatorService.selectedAreas.next(
            Array.from(this.includeSelectedArray)
          );
          this.mapCommunicatorService.excludedAreas.next(
            Array.from(this.excludeSelectedArray)
          );
          this.cd.detectChanges();
        },
        (_) => {
          this.projectsLoading.set(false);
          this.loadProjects();
        }
      );
  }

  public goProjectUp(event: Event) {
    event.stopPropagation();
    this.setAll(false);
    this.list = [];
    this.projects = [];
    this.offset = 0;
    this.offsetTop = 0;
    this.mapCommunicatorService.selectedAreas.next([]);
    this.areaService.count = 0;
    this.cd.detectChanges();
    if (this.currentProject.parent_id) {
      this.projectsLoading.set(true);
      this.newProjectService
        .getProjectById(this.currentProject.parent_id)
        .subscribe((parent) => {
          if (parent.is_root || parent.is_project) {
            this.rootProjectId = parent.id;
            this.getRootProjectDetails(parent.id);
          }

          const skipLoad = parent.is_root;
          this.updateProjectData(parent, skipLoad);

          this.projects = [];
          this.newProjectService
            .getProjectsByPath(parent.path, this.rootState, this.sortState)
            .subscribe(
              (resp) => {
                this.isLoadingProjects = false;
                this.projects = resp || [];

                if (parent.is_root) {
                  if (this.rootState === 'shared') {
                    this.projects = this.projects.filter(
                      (project) =>
                        project?.user?.email !== this.currentUser.email
                    );
                  } else {
                    this.projects = this.projects.filter(
                      (project) =>
                        !project?.user ||
                        project?.user?.email === this.currentUser.email
                    );
                  }
                  this.areasLoading.set(false);
                }

                this.projectsLoading.set(false);
                this.mapCommunicatorService.currentProject.next(
                  this.currentProject
                );
                this.projectSelect.emit(this.currentProject);
                this.cd.detectChanges();
              },
              () => {
                this.isLoadingProjects = false;
                this.projectsLoading.set(false);
                this.areasLoading.set(false);
                this.loadProjects();
              }
            );
        });
    } else {
      this.rootProjectId = undefined;
      this.rootProjectDetailsRequested.clear();
      this.projectsLoading.set(false);
      this.areasLoading.set(false);
      this.goToRoot();
    }

    this.cd.detectChanges();
  }

  public getParentPath(project: Project): string {
    const pathArray = project.path.split('/');
    return pathArray
      .filter((path) => {
        if (path == project.name) return false;
        return !project.skipped_paths.includes(path);
      })
      .join('/');
  }

  private updateProjectData(
    project: Project,
    skipLoad?: boolean,
    afterUpdate?: Function
  ) {
    project.bounding_box &&
      this.mapCommunicatorService.boundingBox.next(project.bounding_box);
    this.currentProject = project;
    this.mapCommunicatorService.currentProject.next(this.currentProject);
    this.projectSelect.emit(project);
    this.updateSubPath(project.path);

    if (project.is_root || project.is_project) {
      if (!this.rootProjectId || this.rootProjectId !== project.id) {
        this.rootProjectId = project.id;
        this.getRootProjectDetails(project.id);
      }
    }

    if (project.is_root) {
      if (!skipLoad) {
        if (this.rootState === 'shared') {
          this.projects = this.projects.filter(
            (project) => project?.user?.email !== this.currentUser.email
          );
        } else {
          this.projects = this.projects.filter(
            (project) =>
              !project?.user || project?.user?.email === this.currentUser.email
          );
        }
        this.areaService.areasList.next([]);
      }
      if (!this.projectsLoading()) {
        this.projectsLoading.set(false);
      }
      this.areasLoading.set(false);
      this.cd.detectChanges();
    } else {
      this.areasLoading.set(true);
      this.cd.detectChanges();
      if (!skipLoad) {
        const folderId = project.id;
        this.areaService
          .getAreasInFolder(project.project_id, folderId, undefined, {
            skip: this.offset,
            limit: this.limit
          })
          .subscribe(
            (response) => {
              this.list = response.items;
              this.areaService.count = response.total;
              this.areasLoading.set(false);
              if (!this.projectsLoading()) {
                this.projectsLoading.set(false);
              }
              this.cd.detectChanges();
              if (afterUpdate) setTimeout((_) => afterUpdate(), 500);
            },
            (_error) => {
              this.areasLoading.set(false);
              if (!this.projectsLoading()) {
                this.projectsLoading.set(false);
              }
              this.cd.detectChanges();
            }
          );
      } else {
        if (afterUpdate) setTimeout((_) => afterUpdate(), 500);
        this.areasLoading.set(false);
        if (!this.projectsLoading()) {
          this.projectsLoading.set(false);
        }
      }
    }
  }

  public deleteProject(id: string, event: Event, is_project?: boolean): void {
    event.stopPropagation();
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: `Are you sure you want to delete this ${
            is_project ? 'project' : 'folder'
          }?`,
          body: `Permanently deleting this ${
            is_project ? 'project' : 'folder'
          } will remove it for you and all users with access. All related data — uploads, downloads, and reports — will be permanently deleted and cannot be restored.`,
          confirmText: 'Delete'
        }
      })
      .afterClosed()
      .subscribe((del) => {
        if (del) {
          this.projectService.deleteProject(id).subscribe(() => {
            this.projects.forEach(() => {
              const index = this.projects.findIndex(
                (project) => project.id === id
              );
              if (index !== -1) this.projects.splice(index, 1);
            });
            this.toastNotificationService.showNotification({
              type: 'success',
              message: `${
                is_project ? 'Project' : 'Folder'
              } deleted successfully.`,
              verticalPosition: 'top'
            });
            this.cd.detectChanges();
          });
        }
      });
  }

  public archiveProject(project: Project): void {
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: `Are you sure you want to ${
            !project.is_archived ? 'archive' : 'unarchive'
          } this project?`,
          body: !project.is_archived
            ? 'Archived projects can be restored anytime.'
            : 'Unarchived projects are saved in "My Projects"',
          confirmText: !project.is_archived ? 'Archive' : 'Unarchive'
        }
      })
      .afterClosed()
      .subscribe((del) => {
        if (del) {
          this.projectService.archiveProject(project).subscribe(() => {
            const index = this.projects.findIndex(
              (prj) => project.id === prj.id
            );
            if (index !== -1) {
              this.projects.splice(index, 1);
              this.cd.detectChanges();
              this.toastNotificationService.showNotification({
                type: 'success',
                message: `Project was successfully ${
                  this.projects[index].is_archived ? 'archived.' : 'unarchived.'
                }`,
                verticalPosition: 'top'
              });
            }
          });
        }
      });
  }

  public generateReport(): void {
    const data: {
      project_id: string;
      root_project_id: string;
      exclude_area_ids?: string[];
      include_area_ids?: string[];
    } = {
      project_id: this.currentProject.project_id,
      root_project_id: this.rootProjectId
    };
    if (this.selectAll) {
      data.exclude_area_ids = Array.from(this.excludeSelectedArray);
    } else {
      data.include_area_ids = Array.from(this.includeSelectedArray);
    }
    this.dialog.open(RiskReportModalComponent, {
      data
    });
  }

  public createProject(): void {
    const createProject = this.dialog.open(CreateProjectModalComponent, {
      data: {
        parent: this.currentProject
      }
    });
    createProject.afterClosed().subscribe((resp) => {
      if (resp) this.loadProjects();
    });
  }

  public editProject(project: Project): void {
    const createProject = this.dialog.open(CreateProjectModalComponent, {
      data: {
        parent: this.currentProject,
        project
      }
    });
    createProject.afterClosed().subscribe((resp) => {
      if (resp) {
        const index = this.projects.findIndex(
          (project) => project.id === resp.id
        );
        this.projects[index] = resp;
      }

      this.cd.detectChanges();
    });
  }

  public moveFolder(project: Project): void {
    if (!project || project.is_project) {
      return;
    }

    const dialogData: MoveDialogData = {
      itemType: 'folder',
      itemId: project.id,
      currentProjectId: project.project_id,
      currentFolderId: this.currentProject?.id || project.parent_id,
      currentFolderName: this.currentProject?.name || 'Root',
      itemName: project.name
    };

    const moveDialog = this.dialog.open(MoveDialogComponent, {
      width: '500px',
      data: dialogData
    });

    moveDialog.afterClosed().subscribe((result) => {
      if (!result || !result.destinationFolderId) {
        return;
      }

      const isCrossProject = this.isCrossProjectMove(
        project,
        result.destinationFolder
      );

      if (isCrossProject) {
        const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
          width: '500px',
          data: {
            header: 'Move to Different Project?',
            body: `Moving "${project.name}" to a different project will copy it instead of moving. The original folder will remain in its current location. Do you want to continue?`,
            confirmText: 'Copy Folder',
            cancelText: 'Cancel'
          }
        });

        confirmDialog.afterClosed().subscribe((confirmed) => {
          if (confirmed) {
            this.toastNotificationService.showNotification({
              type: 'info',
              message:
                'Cross-project moves are not yet supported. Please move within the same project.'
            });
          }
        });
      } else {
        this.projectService
          .moveFolder(project.id, result.destinationFolderId)
          .subscribe({
            next: () => {
              this.toastNotificationService.showNotification({
                type: 'success',
                message: 'Folder moved successfully.'
              });
              this.loadProjects();
            },
            error: (error) => {
              const errorMessage =
                error?.error?.detail ||
                'Failed to move folder. Please try again later.';
              this.toastNotificationService.showNotification({
                type: 'error',
                message: errorMessage
              });
            }
          });
      }
    });
  }

  private isCrossProjectMove(
    sourceFolder: Project,
    destinationFolder: Project
  ): boolean {
    return sourceFolder.project_id !== destinationFolder.project_id;
  }

  public moveArea(area: Area, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const areaId = area.id || area.properties?.id;
    if (!areaId || !this.currentProject) {
      return;
    }

    const dialogData: MoveDialogData = {
      itemType: 'area',
      itemId: areaId,
      currentProjectId: this.currentProject.project_id,
      currentFolderId: this.currentProject.id,
      currentFolderName: this.currentProject.name,
      itemName: area.name
    };

    const moveDialog = this.dialog.open(MoveDialogComponent, {
      width: '500px',
      data: dialogData
    });

    moveDialog.afterClosed().subscribe((result) => {
      if (!result || !result.destinationFolderId) {
        return;
      }

      const isCrossProject =
        this.currentProject.project_id !== result.destinationFolder.project_id;

      if (isCrossProject) {
        const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
          width: '500px',
          data: {
            header: 'Move to Different Project?',
            body: `Moving "${area.name}" to a different project will copy it instead of moving. The original area will remain in its current location. Do you want to continue?`,
            confirmText: 'Copy Area',
            cancelText: 'Cancel'
          }
        });

        confirmDialog.afterClosed().subscribe((confirmed) => {
          if (confirmed) {
            this.toastNotificationService.showNotification({
              type: 'info',
              message:
                'Cross-project moves are not yet supported. Please move within the same project.'
            });
          }
        });
      } else {
        const filter = `id:eq:${areaId}`;
        this.areaService
          .moveAreas(
            this.currentProject.project_id,
            filter,
            result.destinationFolderId
          )
          .subscribe({
            next: (response) => {
              this.toastNotificationService.showNotification({
                type: 'success',
                message: response.message || 'Area moved successfully.'
              });
              this.areaService.clearAreaList();
              this.offset = 0;
              this.updateProjectData(this.currentProject);
              this.mapCommunicatorService.mapCommunicator.next(
                'refetch_areas_for_map'
              );
            },
            error: (error) => {
              const errorMessage =
                error?.error?.detail ||
                'Failed to move area. Please try again later.';
              this.toastNotificationService.showNotification({
                type: 'error',
                message: errorMessage
              });
            }
          });
      }
    });
  }

  public moveAreas(): void {
    const checkedAmount = this.getCheckedAmount();
    if (!checkedAmount || !this.currentProject) {
      return;
    }

    let filter: string;
    if (this.selectAll) {
      if (this.excludeSelectedArray.size > 0) {
        const excludedIds = Array.from(this.excludeSelectedArray).join('|');
        filter = `id:not_in:${excludedIds}`;
      } else {
        filter = `folder_id:eq:${this.currentProject.id}`;
      }
    } else {
      const includedIds = Array.from(this.includeSelectedArray).join('|');
      filter = `id:in:${includedIds}`;
    }

    const dialogData: MoveDialogData = {
      itemType: 'areas',
      itemIds: this.selectAll ? [] : Array.from(this.includeSelectedArray),
      currentProjectId: this.currentProject.project_id,
      currentFolderId: this.currentProject.id,
      currentFolderName: this.currentProject.name,
      itemsCount: checkedAmount
    };

    const moveDialog = this.dialog.open(MoveDialogComponent, {
      width: '500px',
      data: dialogData
    });

    moveDialog.afterClosed().subscribe((result) => {
      if (!result || !result.destinationFolderId) {
        return;
      }

      const isCrossProject =
        this.currentProject.project_id !== result.destinationFolder.project_id;

      if (isCrossProject) {
        const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
          width: '500px',
          data: {
            header: 'Move to Different Project?',
            body: `Moving ${checkedAmount} area${
              checkedAmount > 1 ? 's' : ''
            } to a different project will copy them instead of moving. The original areas will remain in their current location. Do you want to continue?`,
            confirmText: 'Copy Areas',
            cancelText: 'Cancel'
          }
        });

        confirmDialog.afterClosed().subscribe((confirmed) => {
          if (confirmed) {
            this.toastNotificationService.showNotification({
              type: 'info',
              message:
                'Cross-project moves are not yet supported. Please move within the same project.'
            });
          }
        });
      } else {
        this.areaService
          .moveAreas(
            this.currentProject.project_id,
            filter,
            result.destinationFolderId
          )
          .subscribe({
            next: (response) => {
              this.toastNotificationService.showNotification({
                type: 'success',
                message:
                  response.message ||
                  `Successfully moved ${response.moved_count} area(s).`
              });

              this.selectAll = false;
              this.includeSelectedArray.clear();
              this.excludeSelectedArray.clear();
              this.mapCommunicatorService.setAllAreas.next(false);

              this.areaService.clearAreaList();
              this.offset = 0;
              this.updateProjectData(this.currentProject);
              this.mapCommunicatorService.mapCommunicator.next(
                'refetch_areas_for_map'
              );
            },
            error: (error) => {
              const errorMessage =
                error?.error?.detail ||
                'Failed to move area(s). Please try again later.';
              this.toastNotificationService.showNotification({
                type: 'error',
                message: errorMessage
              });
            }
          });
      }
    });
  }

  public onApplyFilter(filterEvent: {
    filters?: FilterField[] | FilterGroup;
    sorts?: SortConfig[];
  }) {
    if (!this.currentProject || this.currentProject.is_root) {
      return;
    }

    this.offset = 0;
    this.offsetTop = 0;
    this.filterTrigger.closeMenu();
    this.areasLoading.set(true);
    this.cd.detectChanges();
    this.mapCommunicatorService.updateMapAreas.next(true);

    // CRITICAL FIX: Use currentProject.id as folderId, not rootProjectId
    const folderId = this.currentProject.id;
    const projectId = this.currentProject.project_id;

    this.areaService
      .getAreasWithAdvancedFilters(projectId, folderId, {
        filters: filterEvent.filters,
        sorts: filterEvent.sorts,
        pagination: {
          skip: this.offset,
          limit: this.limit
        }
      })
      .subscribe(
        (response) => {
          this.list = response.items;
          this.areaService.count = response.total;
          this.areasLoading.set(false);
          this.cd.detectChanges();
        },
        (_error) => {
          this.areasLoading.set(false);
          this.cd.detectChanges();
        }
      );
  }

  public closeFilter() {
    this.filterTrigger.closeMenu();
  }

  public getAppliedFiltersLength() {
    return (
      this.areaService.filterBody.tags.length +
      this.areaService.filterBody.metadata.length
    );
  }

  public loadAreas(loadTop?: boolean) {
    if (this.areasLoadingByScroll() || this.loadingData()) return;
    if (!this.currentProject || this.currentProject.is_root) return;
    if (loadTop) {
      if (this.offsetTop == 0) return;
      this.offsetTop -= this.limit;
    } else {
      if (this.offset >= this.areaService.count) return;
      this.offset += this.limit;
    }
    if (this.offsetTop < 0) this.offsetTop = 0;
    this.areasLoadingByScroll.set(true);
    const folderId = this.currentProject.id;
    this.areaService
      .getAreasInFolder(this.currentProject.project_id, folderId, undefined, {
        skip: loadTop ? this.offsetTop : this.offset,
        limit: this.limit
      })
      .subscribe(
        (response) => {
          this.list = loadTop
            ? [...response.items, ...this.list]
            : [...this.list, ...response.items];
          this.areaService.count = response.total;
          this.areasLoadingByScroll.set(false);
          this.cd.detectChanges();
        },
        (_error) => {
          // Removed console.error for production
          this.areasLoadingByScroll.set(false);
          this.cd.detectChanges();
        }
      );
  }

  public transformSearchPath(
    path: string,
    item: Project | AreaSearchHit
  ): Array<{ icon?: string; label: string; final?: boolean }> {
    if (!path) return [{ label: 'Unknown destination', final: true }];
    const isShared = item.user && item.user.email !== this.currentUser.email;
    const prefixIcon = isShared ? 'team' : 'project';
    const result: Array<{ icon?: string; label: string; final?: boolean }> = [];

    const pathArray = path.split('/');
    if (pathArray.length <= 2) {
      result.push({
        icon: `${prefixIcon}.svg`,
        label: isShared ? 'Shared with me' : 'My Projects'
      });
      result.push({ label: pathArray[pathArray.length - 1] });
    } else {
      result.push({ icon: `${prefixIcon}.svg`, label: '' });
      result.push({ label: '...' });
      result.push({ label: pathArray[pathArray.length - 1] });
    }

    return result;
  }

  public goToItem(item: Project | AreaSearchHit) {
    this.searchResults = [];
    this.showSearch = false;
    if ((item as any)?.project?.path) {
      const areaItem = item as AreaSearchHit;
      if (this.currentProject?.path === (areaItem as any)?.project?.path) {
        const targetIndex = this.list.findIndex((area) => area.id === item.id);
        if (targetIndex == -1) {
          this.list = [];
          this.areaService
            .getAreaPositionById(
              (areaItem as AreaSearchHit).id as unknown as number,
              this.currentProject.project_id,
              this.currentProject.id
            )
            .subscribe((area) => {
              this.areaService.areasList.next([]);
              this.list = area.features;
              this.offsetTop = area.meta.current_window.start;
              this.offset = area.meta.current_window.end;
              this.cd.detectChanges();
              this.updateProjectData(this.currentProject, true, () => {
                this.highlightCallback(item as any);
              });
            });
        } else {
          this.highlightCallback(item as any);
        }
      } else {
        this.projectsLoading.set(true);
        this.cd.detectChanges();
        this.newProjectService
          .getProjectsByPath(
            (areaItem as any).project.path,
            this.rootState,
            this.sortState
          )
          .subscribe(
            (resp) => {
              this.areaService.areasList.next([]);
              this.list = [];
              this.areaService
                .getAreaPositionById(
                  (areaItem as AreaSearchHit).id as unknown as number,
                  resp[0].project_id,
                  resp[0].id
                )
                .subscribe((area) => {
                  this.offset = area.meta.current_window.end;
                  this.offsetTop = area.meta.current_window.start;
                  this.list = area.features;
                  this.areaService.areasList.next(area.features);
                  this.updateProjectData(resp[0], true, () => {
                    this.highlightCallback(item as any);
                  });
                });
            },
            (_) => {
              this.projectsLoading.set(false);
              this.toastNotificationService.showNotification({
                type: 'error',
                message: 'This area stored in archive project'
              });
            }
          );
      }
    } else if (this.isProjectSearchResult(item)) {
      this.navigateToProjectSearchResult(item);
    }
    this.searchAreaCtrl.setValue('');
  }

  private navigateToProjectSearchResult(projectResult: Project) {
    if (!projectResult?.id) {
      return;
    }

    this.list = [];
    this.projectsLoading.set(true);
    this.cd.detectChanges();

    this.projectService
      .getProjectById(projectResult.id)
      .pipe(take(1))
      .subscribe({
        next: (project) => {
          this.openProject(project);
        },
        error: () => {
          this.projectsLoading.set(false);
          this.cd.detectChanges();
          this.toastNotificationService.showNotification({
            type: 'error',
            message: 'Unable to open the selected folder. Please try again.'
          });
        }
      });
  }

  public highlightCallback = (item) => {
    const targetIndex = this.list.findIndex((area) => area.id === item.id);
    this.mapCommunicatorService.geometryToFocus.next({
      geometry: item.geometry
    });
    if (targetIndex !== -1) {
      this.list[targetIndex].highlighted = true;
      this.cd.detectChanges();
      Array.from(this.listContainer.nativeElement.children)
        .find((ch) => ch.id === this.list[targetIndex].id)
        ?.scrollIntoView();
      setTimeout((_) => (this.list[targetIndex].highlighted = false), 2000);
      this.cd.detectChanges();
    }
  };

  public share(area: Project, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.dialog
      .open(ShareModalComponent<ChildProject>, {
        data: {
          entity: area,
          canShare: !this.currentProject
        }
      })
      .afterClosed()
      .subscribe((updated) => {
        if (updated) {
          this.loadProjects();
        }
      });
  }

  public unfollow(id: string): void {
    this.dialog
      .open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
        width: '640px',
        data: {
          header: 'Are you sure you want to remove this project from the list?',
          body: 'This project will be removed from your list. Users with access will still be able to view it.',
          confirmText: 'Yes, remove project',
          cancelText: 'No'
        }
      })
      .afterClosed()
      .subscribe(() => {
        this.newProjectService.getShared().subscribe((resp) => {
          if (!resp) return;
          const shareToDelete = resp.find((share) => share.project_id === id);
          this.newProjectService.deleteShare(shareToDelete.id).subscribe(
            (_) => {
              this.toastNotificationService.showNotification({
                type: 'success',
                message: 'Project was successfully removed from your list.',
                verticalPosition: 'top'
              });
            },
            (error) => {
              this.toastNotificationService.showNotification({
                type: 'error',
                message: error.error.detail,
                verticalPosition: 'top'
              });
            }
          );
        });
      });
  }

  public showResults(state: boolean) {
    setTimeout(() => {
      this.showSearchResults = state;
      this.cd.detectChanges();
    }, 500);
  }

  public hasMenuActions(item: Project): boolean {
    if (!item) return false;

    const canEdit =
      !item.is_archived &&
      (item.is_project
        ? item.is_owned_by_current_user
        : item.effective_permissions?.can_update);

    const canShare = !item.is_archived && item.is_owned_by_current_user;

    const canArchive = item.is_owned_by_current_user;

    const canDelete =
      !item.is_archived &&
      (item.is_project
        ? item.is_owned_by_current_user
        : item.effective_permissions?.can_delete);

    const canUnfollow = !item.is_archived && !item.is_owned_by_current_user;
    return canEdit || canShare || canArchive || canDelete || canUnfollow;
  }

  protected readonly parent = parent;
}
